/**
 * Service layer — Construction Hub (cross-project, read only).
 *
 * Wraps GET /api/construction-hub?view=… (Clerk-authed, any signed-in user). Every edit stays in
 * its existing home — the project's Construction section, the Contacts directory — so this file
 * only reads. Contractor organizations themselves come from services/contacts.js.
 * See docs/CONSTRUCTION_HUB.md.
 */
import { getOrganizations } from './contacts.js'

// OutlineView installs window.__clerkGetToken in an effect; a hub opened straight from a
// ?ws=construction link mounts first and can race it (the services/_api.js wait, same shape).
async function clerkToken() {
  for (let i = 0; i < 40; i++) {
    if (typeof window.__clerkGetToken === 'function') return window.__clerkGetToken()
    await new Promise(r => setTimeout(r, 150))
  }
  throw new Error('Not signed in yet. Reload the page.')
}

async function view(name) {
  const token = await clerkToken()
  const res = await fetch('/api/construction-hub?view=' + encodeURIComponent(name), {
    headers: { 'Authorization': 'Bearer ' + token },
  })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b.error || 'Construction Hub request failed: ' + res.status)
  }
  return res.json()
}

/** Open RFIs, open submittals, unissued ASIs, open Proposal Requests, across every project. */
export function getOpenConstructionItems() {
  return view('open').then(d => d.items || [])
}

/** Every construction contract with its Contractor Portal state. */
export function getAllConstructionContracts() {
  return view('contracts').then(d => d.contracts || [])
}

/** What contractors sent that nobody has opened yet, plus forwarded emails being held. */
export function getConstructionIntake() {
  return view('intake').then(d => ({ arrivals: d.arrivals || [], emails: d.emails || [] }))
}

// The Organizations `Type` values that mean "builds things for us" (Type is a multipleSelects).
export const CONTRACTOR_ORG_TYPES = ['General Contractor', 'Construction Manager', 'Subcontractor']

/**
 * Contractor organizations, each with its contracts, bids and bid invitations across projects.
 * An organization shows if its Type is a contractor type OR it has any construction history,
 * so a GC whose Type was never set in Contacts still appears.
 */
export async function getContractorLibrary() {
  const [orgs, hist] = await Promise.all([getOrganizations(), view('contractors')])
  const history = hist.history || {}
  const out = []
  ;(orgs || []).forEach(o => {
    const f = o.fields || {}
    const types = [].concat(f['Type'] || [])
    const h = history[o.id]
    if (!h && !types.some(t => CONTRACTOR_ORG_TYPES.includes(t))) return
    out.push({
      id: o.id, name: f['Organization Name'] || (h && h.name) || '', types,
      phone: f['Phone'] || '', email: f['General Email'] || '', website: f['Website'] || '',
      contracts: h ? h.contracts : [], bids: h ? h.bids : [], invites: h ? h.invites : [],
    })
  })
  // History pointing at an org id that is not in Organizations any more (deleted or never synced).
  Object.entries(history).forEach(([id, h]) => {
    if (out.some(o => o.id === id)) return
    out.push({ id, name: h.name || 'Unknown organization', types: [], phone: '', email: '', website: '',
      contracts: h.contracts, bids: h.bids, invites: h.invites, missing: true })
  })
  return out.sort((a, b) => a.name.localeCompare(b.name))
}
